// ProfileGallery — Masonry grid, hover zoom, lightbox, filter
import { X, ZoomIn } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface GalleryItem {
  id: number | string
  url: string
  title?: string
  type: 'image' | 'ai_image' | 'photo'
  created_at?: string
}

interface Props {
  items: GalleryItem[]
  loading?: boolean
}

type Filter = 'all' | 'image' | 'ai_image' | 'photo'

export default function ProfileGallery({ items, loading }: Props) {
  const [filter, setFilter] = useState<Filter>('all')
  const [lightbox, setLightbox] = useState<GalleryItem | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const filtered = filter === 'all' ? items : items.filter(i => i.type === filter)

  // Intersection observer — fade-in animasyonu
  const cardRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        entries => entries.forEach(e => {
          if (e.isIntersecting) {
            ;(e.target as HTMLElement).style.opacity = '1'
            ;(e.target as HTMLElement).style.transform = 'translateY(0)'
          }
        }),
        { threshold: 0.1 }
      )
    }
    observerRef.current.observe(node)
  }, [])

  useEffect(() => {
    return () => { observerRef.current?.disconnect() }
  }, [])

  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'ai_image', label: 'AI Generated' },
    { key: 'photo', label: 'Photos' },
  ]

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary, #8b949e)', fontSize: 13 }}>Loading gallery...</div>
  }

  if (items.length === 0) {
    return <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-secondary, #8b949e)', fontSize: 14 }}>No gallery items yet</div>
  }

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600,
              cursor: 'pointer',
              background: filter === f.key ? 'rgba(102,126,234,0.15)' : 'transparent',
              color: filter === f.key ? '#667eea' : 'var(--text-secondary, #8b949e)',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Masonry grid */}
      <div style={{
        columns: 'auto 240px', columnGap: 12, width: '100%',
      }}>
        {filtered.map((item, idx) => (
          <div
            key={item.id}
            ref={cardRef}
            onClick={() => setLightbox(item)}
            style={{
              breakInside: 'avoid', marginBottom: 12, borderRadius: 12,
              overflow: 'hidden', position: 'relative', cursor: 'pointer',
              border: '1px solid var(--border-default, #30363d)',
              opacity: 0, transform: 'translateY(20px)',
              transition: `opacity 0.4s ease ${idx * 0.05}s, transform 0.4s ease ${idx * 0.05}s`,
            }}
            onMouseEnter={e => {
              const el = e.currentTarget
              el.style.transform = 'scale(1.02)'
              el.style.boxShadow = '0 8px 32px rgba(102,126,234,0.2)'
              const overlay = el.querySelector('[data-overlay]') as HTMLElement
              if (overlay) overlay.style.opacity = '1'
            }}
            onMouseLeave={e => {
              const el = e.currentTarget
              el.style.transform = 'translateY(0)'
              el.style.boxShadow = 'none'
              const overlay = el.querySelector('[data-overlay]') as HTMLElement
              if (overlay) overlay.style.opacity = '0'
            }}
          >
            <img
              src={item.url}
              alt={item.title || 'Gallery item'}
              loading="lazy"
              style={{ width: '100%', display: 'block' }}
            />
            {/* Hover overlay */}
            <div
              data-overlay=""
              style={{
                position: 'absolute', inset: 0, opacity: 0,
                background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.7) 100%)',
                display: 'flex', alignItems: 'flex-end', padding: 14,
                transition: 'opacity 0.25s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.title || 'Untitled'}
                </span>
                <ZoomIn size={16} color="#fff" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, cursor: 'zoom-out',
          }}
        >
          <button
            onClick={() => setLightbox(null)}
            aria-label="Close lightbox"
            style={{
              position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.1)',
              border: 'none', borderRadius: '50%', width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff',
            }}
          >
            <X size={20} />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.title || ''}
            style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 12, objectFit: 'contain' }}
          />
          {lightbox.title && (
            <p style={{ position: 'absolute', bottom: 32, color: '#fff', fontSize: 15, fontWeight: 600 }}>
              {lightbox.title}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
